// sw-register.ts — auto-register the platform service worker on every glow.
//
// D1 (universal install): every Lit glow is installable, so the SDK registers
// the platform SW (/_backlit/sw.js, root scope) on load for EVERY glow —
// independent of whether push is enabled. The edge serves the file with
// Service-Worker-Allowed: / so it can claim root scope; the SW handles PWA
// install, push delivery, and the pushsubscriptionchange self-heal. The edge
// only SERVES the file; the SDK is what registers it.
//
// Best-effort and silent: registration failures are swallowed (.catch) so a
// non-push / degraded-browser glow never breaks. Separate from
// push.subscribe(), which registers on-demand as part of enrolling a user (a
// double register is idempotent — the browser dedupes by scope+url).
//
// Guard order mirrors heartbeat.ts exactly (the host check fails OPEN in a
// non-browser harness, so the window existence check MUST come first):
//   1. already started → no-op (idempotent against a double <script src>)
//   2. non-browser (no window/navigator, or no serviceWorker) → no-op
//   3. off a deployed glow host → no-op

import { isBacklitHost } from "./host.js";

// started gates double-init (index.ts evaluates once, but a double-load would
// re-run it). It is module-level like heartbeat.ts's `started`.
let started = false;

// initServiceWorker is the auto-start entry point, called once from index.ts
// after initHeartbeat(). It registers the platform SW best-effort and returns
// immediately (it does not await the registration — nothing on the page depends
// on it).
export function initServiceWorker(): void {
  if (started) return;
  if (typeof window === "undefined" || typeof navigator === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  if (!isBacklitHost()) return;

  started = true;

  try {
    // Register at root scope. The edge serves /_backlit/sw.js with
    // Service-Worker-Allowed: / so the non-root path can still claim "/".
    // .catch swallows every failure — a glow that can't register a SW (an
    // insecure context, a browser without SW support that still exposes the
    // property, a transient fetch error) must keep working.
    void navigator.serviceWorker.register("/_backlit/sw.js", { scope: "/" }).catch(() => {});
  } catch {
    /* ignore — register itself threw synchronously (exotic harness) */
  }
}

// _resetSWRegisterForTest clears the module-level state so each test starts
// clean — mirrors heartbeat.ts's _resetHeartbeatForTest precedent.
export function _resetSWRegisterForTest(): void {
  started = false;
}
